/**
 * ScanProgress — Jul-2026 (User-Wunsch).
 * Live-Fortschrittsanzeige mit Schritten für alle KI-Scan-Verarbeitungen
 * (Scan-Erfassung, Lieferschein, Dokumenten-Postausgang). Zeigt Sekunden-
 * zähler + Schritt-Status, damit klar ist: es hängt nichts.
 */
import { useEffect, useState } from "react";
import { Loader2, Check, FileUp, Brain, SearchCheck, Sparkles } from "lucide-react";

const STEPS = [
    { key: "upload", label: "Dokument hochladen", icon: FileUp },
    { key: "ai", label: "KI liest das Dokument (Texterkennung)", icon: Brain, at: 2 },
    { key: "match", label: "Kunde, Fahrzeug & Artikel abgleichen", icon: SearchCheck, at: 10 },
    { key: "finish", label: "Ergebnis aufbereiten", icon: Sparkles, at: 20 },
];

export const ScanProgress = ({ active, fileName, phase = "processing", pages = null, dark = false, testId = "scan-progress" }) => {
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        if (!active) { setElapsed(0); return undefined; }
        const t = setInterval(() => setElapsed((s) => s + 1), 1000);
        return () => clearInterval(t);
    }, [active]);
    if (!active) return null;

    // Aug-2026 (User-Wunsch): echter Seiten-Fortschritt beim Lieferschein-Scan
    const hasPages = pages && Number(pages.pages_total) > 1;
    // Aug-2026 (Hänger-Fix): echte Buchungs-Phase — „Beleg X von Y wird verbucht"
    const isBooking = pages?.stage === "booking" && Number(pages.docs_total) > 0;
    const bookingLabel = isBooking
        ? `Ergebnis aufbereiten — Beleg ${Math.min((Number(pages.docs_done) || 0) + 1, Number(pages.docs_total))} von ${pages.docs_total} wird verbucht…`
        : null;
    const pct = isBooking
        ? Math.min(95, 80 + Math.round(15 * (Number(pages.docs_done) || 0) / Number(pages.docs_total)))
        : hasPages
            ? Math.min(95, 10 + Math.round(80 * (Number(pages.pages_done) || 0) / Number(pages.pages_total)))
            : Math.min(95, Math.round(100 * (1 - Math.exp(-elapsed / 14))) + 5);
    const aiLabel = hasPages
        ? `KI liest das Dokument — Seite ${Math.min((Number(pages.pages_done) || 0) + 1, Number(pages.pages_total))} von ${pages.pages_total}`
        : null;
    const stepDone = (s, idx) => {
        if (s.key === "upload") return phase !== "upload";
        if (phase === "upload") return false;
        if (isBooking) return s.key !== "finish";
        return elapsed >= (STEPS[idx + 1]?.at ?? Infinity);
    };
    const firstOpen = STEPS.findIndex((s, i) => !stepDone(s, i));

    const wrap = dark
        ? "border-2 border-purple-500 bg-purple-700 text-white"
        : "border border-gray-200 bg-white text-gray-800";
    const sub = dark ? "text-purple-100" : "text-gray-500";
    const barBg = dark ? "bg-purple-900/60" : "bg-gray-100";

    return (
        <div className={`mt-3 rounded-sm px-4 py-3.5 shadow-lg ${wrap}`} data-testid={testId}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="font-black text-sm uppercase tracking-wider inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> KI verarbeitet {fileName ? `„${fileName}"` : "das Dokument"}…
                </div>
                <span className={`text-xs font-mono-num font-bold ${sub}`} data-testid={`${testId}-elapsed`}>{elapsed}s</span>
            </div>
            <div className={`h-1.5 rounded-full mt-2.5 overflow-hidden ${barBg}`}>
                <div className="h-full bg-[#FFCC00] transition-all duration-1000" style={{ width: `${pct}%` }} />
            </div>
            <ul className="mt-2.5 space-y-1">
                {STEPS.map((s, i) => {
                    const done = stepDone(s, i);
                    const current = i === firstOpen;
                    const Icon = s.icon;
                    return (
                        <li key={s.key} className={`flex items-center gap-2 text-xs ${done ? "opacity-90" : current ? "font-bold" : "opacity-40"}`} data-testid={`${testId}-step-${s.key}`}>
                            {done
                                ? <Check className="w-3.5 h-3.5 text-green-400 shrink-0" />
                                : current
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                                    : <Icon className="w-3.5 h-3.5 shrink-0" />}
                            <span className={current ? "animate-pulse" : ""} data-testid={`${testId}-step-label-${s.key}`}>
                                {s.key === "finish" && bookingLabel ? bookingLabel
                                    : s.key === "ai" && aiLabel ? aiLabel : s.label}
                            </span>
                        </li>
                    );
                })}
            </ul>
            <div className={`text-[10px] mt-2 ${sub}`}>
                Läuft sicher im Hintergrund weiter — kein Timeout, das Ergebnis erscheint automatisch.
            </div>
        </div>
    );
};

export default ScanProgress;
